import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import PublicNav from '../components/dna/PublicNav';
import Footer from '../components/dna/Footer';
import { useAuthStatus } from '../hooks/useAuthStatus';

const Feature = ({ icon, title, text, delay = 0 }) => (
  <article className="feature-card" data-aos="fade-up" data-aos-delay={delay}>
    <div className="feature-icon"><i className={`fa-solid ${icon}`} /></div>
    <h3>{title}</h3>
    <p>{text}</p>
    <i className="fa-solid fa-arrow-right feature-arrow" />
  </article>
);

const Step = ({ number, title, text, icon, delay = 0 }) => (
  <article className="step-card step-card-new" data-aos="fade-up" data-aos-delay={delay}>
    <span className="step-ghost" aria-hidden="true">{number}</span>
    <div className="step-top">
      <span className="step-icon"><i className={`fa-solid ${icon}`} /></span>
    </div>
    <h3>{title}</h3>
    <p>{text}</p>
    <div className="step-foot">
      <span className="step-dot" aria-hidden="true" />
      <span className="step-rule" aria-hidden="true" />
      <span className="step-badge">Étape {number}</span>
    </div>
  </article>
);

const PricingCard = ({ free = false, premiumConfig, delay = 0 }) => {
  const isLoggedIn = useAuthStatus();
  const premTracks = premiumConfig?.premium?.max_tracks_per_month || 30;
  const premCommission = Math.round((premiumConfig?.premium?.commission_rate || 0.15) * 100);
  const freeTracks = premiumConfig?.free?.max_tracks_per_month || 3;
  const freeCommission = Math.round((premiumConfig?.free?.commission_rate || 0.20) * 100);
  const premPrice = premiumConfig?.premium_price_ariary ? premiumConfig.premium_price_ariary.toLocaleString() : '2 000';

  return (
    <article className={`pricing-card ${free ? '' : 'pricing-featured'}`} data-aos="fade-up" data-aos-delay={delay}>
      <div className="pricing-head">
        <div>
          <span className="plan-label">{free ? 'Free' : 'Premium'}</span>
          <p>{free ? 'Pour démarrer sans engagement.' : 'Pour passer à la vitesse supérieure.'}</p>
        </div>
        {!free && <span className="recommended"><i className="fa-solid fa-sparkles" style={{ fontSize: '.65rem' }} /> Recommandé</span>}
      </div>
      <div className="price">
        <strong>{free ? '0' : premPrice}</strong>
        <span>Ar / mois<br /><small>{free ? 'gratuit à vie' : 'paiement simple, sans auto-débit'}</small></span>
      </div>
      <div className="price-badge">{free ? `Commission ${freeCommission}% sur les ventes` : `Commission ${premCommission}% seulement — paiement J+15`}</div>
      <ul>
        {(free
          ? [`${freeTracks} tracks/mois pour tester`, 'Albums illimités', 'Portail de vente Mobile Money', 'Chiffrement AES anti-piratage', 'Page artiste publique', 'Bot Telegram basique', 'Statistiques de base']
          : [`${premTracks} tracks/mois — 10x plus`, 'Albums illimités', 'Singles gratuits', 'Streaming HLS 64k + 96k', 'Paroles LRC synchronisées', 'Analytics avancés', 'Support prioritaire', 'Bot Telegram avancé']
        ).map((x) => <li key={x}><i className="fa-solid fa-check" style={{ color: '#189b69' }} /> {x}</li>)}
      </ul>
      <Link to={isLoggedIn ? (free ? '/dashboard' : '/dashboard/premium') : '/register'} className={`button ${free ? 'button-outline' : 'button-crimson'}`}>
        {isLoggedIn ? (free ? 'Aller à mon studio' : 'Passer Premium') : (free ? 'Commencer gratuitement' : 'Passer Premium')} <i className="fa-solid fa-arrow-right" style={{ fontSize: '.7rem' }} />
      </Link>
    </article>
  );
};

const Home = () => {
  const [premiumConfig, setPremiumConfig] = useState(null);
  const isLoggedIn = useAuthStatus();

  useEffect(() => {
    async function loadConfig() {
      try {
        const res = await fetch('/api/subscriptions/features');
        if (res.ok) setPremiumConfig(await res.json());
      } catch { /* silence */ }
    }
    loadConfig();
  }, []);

  useEffect(() => {
    AOS.init({ duration: 650, once: true, offset: 70 });
  }, []);

  useEffect(() => {
    AOS.refresh();
  }, [premiumConfig]);

  return (
    <div className="site-shell home-page">
      <PublicNav />
      <main>
        <section className="hero-section">
          <div className="hero-grid" />
          <div className="hero-glow hero-glow-one" />
          <div className="hero-glow hero-glow-two" />
          <div className="hero-copy" data-aos="fade-up">
            <div className="eyebrow"><span className="eyebrow-dot" /> L’infrastructure musicale N°1 à Madagascar</div>
            <h1>La musique a du prix,<br /><em>donnons-lui de la valeur.</em></h1>
            <p className="hero-lead">La première plateforme malgache qui sécurise vos créations et simplifie l’achat pour vos fans via Mobile Money.</p>
            <div className="hero-actions">
              {isLoggedIn ? (
                <Link to="/dashboard" className="button button-crimson"><i className="fa-solid fa-table-columns" /> Accéder à mon studio</Link>
              ) : (
                <Link to="/register" className="button button-crimson"><i className="fa-solid fa-bolt" /> Lancer mon projet</Link>
              )}
              <a href="#ecosystem" className="text-link"><span className="play-circle"><i className="fa-solid fa-play" style={{ fontSize: '.6rem' }} /></span> Voir le fonctionnement</a>
            </div>
            <div className="social-proof">
              <div className="avatar-stack"><span>T</span><span>F</span><span>R</span><span>N</span></div>
              <span><strong>+200 artistes</strong> nous font confiance à travers Madagascar</span>
            </div>
          </div>
          <div className="hero-art" data-aos="zoom-in" data-aos-delay="150">
            <div className="art-frame">
              <div className="art-topline"><span>PASS’IO / STUDIO</span><span>NO. 001</span></div>
              <div className="art-portrait">
                <div className="portrait-sun" />
                <div className="portrait-head" />
                <div className="portrait-body" />
                <div className="portrait-note">SOUND<br /><b>HAS VALUE</b></div>
              </div>
              <div className="art-bottom"><span>PROTECT YOUR<br />CATALOGUE</span><span className="art-wave">∿∿∿</span></div>
            </div>
            <div className="floating-chip chip-top"><i className="fa-solid fa-shield-halved" /> AES-256 protected</div>
            <div className="floating-chip chip-bottom"><i className="fa-solid fa-music" /> Built for Madagascar</div>
          </div>
        </section>

        <div className="trust-strip" data-aos="fade-up">
          <span>Utilisé par les artistes qui exigent la qualité</span>
          <div className="trust-names"><b>Laza Records</b><b>Studio Mada</b><b>Tana Beats</b><b>RM Production</b><b>Ody Music</b></div>
        </div>

        <section id="concept" className="section section-concept">
          <div className="section-heading" data-aos="fade-up">
            <div>
              <div className="eyebrow">La nouvelle norme</div>
              <h2>Ne soyez plus<br /><em>un simple artiste.</em></h2>
            </div>
            <p>Le piratage a tué l’industrie. Pass’io est l’arme de riposte. Ce n’est pas qu’une plateforme — c’est le seul sanctuaire où votre musique est protégée, valorisée et vendue.</p>
          </div>
          <div className="feature-grid">
            <Feature icon="fa-crown" title="Prestige & Exclusivité" text="Rejoignez l’élite des artistes qui construisent une carrière durable." delay={0} />
            <Feature icon="fa-shield-halved" title="Sécurité Inviolable" text="Une technologie de chiffrement propriétaire qui rend le partage illégal techniquement impossible." delay={90} />
            <Feature icon="fa-credit-card" title="Mobile Money Natif" text="Vos fans achètent en 30 secondes via MVola ou Orange Money — zéro friction." delay={180} />
            <Feature icon="fa-chart-line" title="Analytics Avancés" text="Visualisez vos ventes et vos tendances en temps réel pour décider mieux." delay={270} />
          </div>
        </section>

        <section id="ecosystem" className="section ecosystem-section">
          <div className="center-heading" data-aos="fade-up">
            <div className="eyebrow">Écosystème complet</div>
            <h2>Un écosystème <em>Premium</em></h2>
            <p>Tout ce dont vous avez besoin pour gérer votre carrière, au même endroit.</p>
          </div>
          <div className="eco-grid">
            <div className="eco-card eco-card-large" data-aos="fade-up" data-aos-delay={0}>
              <div className="eco-visual sale-visual">
                <div className="mini-window">
                  <div className="mini-nav" />
                  <div className="mini-cover" style={{ background: 'linear-gradient(135deg,#d2a49b,#4b1c27)' }} />
                  <div className="mini-lines"><i /><i /><i /></div>
                </div>
                <div className="mini-bubble"><i className="fa-solid fa-check" /> Paiement confirmé</div>
              </div>
              <div className="eco-copy">
                <span className="card-kicker">01 / Vente directe</span>
                <h3>Portail de Vente</h3>
                <p>Une interface élégante où vos fans achètent vos projets en quelques clics.</p>
                <span className="feature-list"><i className="fa-solid fa-check" /> Page artiste personnalisée</span>
                <span className="feature-list"><i className="fa-solid fa-check" /> Paiement Mobile Money intégré</span>
                <span className="feature-list"><i className="fa-solid fa-check" /> Livraison instantanée</span>
              </div>
            </div>
            <div className="eco-card eco-card-dark" data-aos="fade-up" data-aos-delay={120}>
              <span className="featured-badge">Phare</span>
              <div className="eco-visual player-visual">
                <div className="mini-player">
                  <div className="mini-player-cover">
                    <span>PASS’IO</span>
                    <b><i className="fa-solid fa-play" /></b>
                  </div>
                  <div className="mini-player-meta"><i /><i /></div>
                  <div className="mini-eq"><i /><i /><i /><i /><i /><i /><i /></div>
                  <div className="mini-progress"><span /></div>
                  <div className="mini-controls">
                    <i className="fa-solid fa-backward-step" />
                    <b><i className="fa-solid fa-pause" /></b>
                    <i className="fa-solid fa-forward-step" />
                  </div>
                </div>
              </div>
              <div className="eco-copy">
                <span className="card-kicker">02 / Expérience fan</span>
                <h3>Lecteur Sécurisé</h3>
                <p>Une expérience immersive pour écouter votre musique, en ligne et hors ligne.</p>
                <span className="feature-list"><i className="fa-solid fa-check" /> Lecture offline HD</span>
                <span className="feature-list"><i className="fa-solid fa-check" /> DRM propriétaire</span>
              </div>
            </div>
            <div className="eco-card eco-card-gold" data-aos="fade-up" data-aos-delay={240}>
              <div className="telegram-mark"><i className="fa-brands fa-telegram" style={{ fontSize: 38 }} /></div>
              <div className="eco-copy">
                <span className="card-kicker">03 / Gestion simplifiée</span>
                <h3>Bot de Gestion</h3>
                <p>Gérez vos stocks et vos revenus directement depuis Telegram.</p>
                <span className="feature-list"><i className="fa-solid fa-check" /> Notifications en temps réel</span>
                <span className="feature-list"><i className="fa-solid fa-check" /> Retrait Mobile Money</span>
              </div>
            </div>
          </div>
        </section>

        <section className="section steps-section">
          <div className="center-heading" data-aos="fade-up">
            <div className="eyebrow">Démarrage rapide</div>
            <h2>En 3 étapes, vous êtes <em>prêt.</em></h2>
          </div>
          <div className="steps-grid">
            <Step number="01" icon="fa-user-plus" title="Créez votre compte" text="Inscrivez-vous en moins de 2 minutes et préparez votre espace artiste." delay={0} />
            <Step number="02" icon="fa-cloud-arrow-up" title="Publiez votre musique" text="Déposez vos albums. Notre système les chiffre et les prépare à la vente." delay={120} />
            <Step number="03" icon="fa-wallet" title="Encaissez vos gains" text="Vos fans achètent, vous recevez une notification et le virement sur votre Mobile Money." delay={240} />
          </div>
        </section>

        <section id="pricing" className="section pricing-section">
          <div className="center-heading" data-aos="fade-up">
            <div className="eyebrow">Tarification</div>
            <h2>Des plans pour chaque <em>ambition</em></h2>
            <p>Commencez gratuitement, passez Premium quand vous êtes prêt à accélérer.</p>
          </div>
          <div className="pricing-grid">
            <PricingCard free premiumConfig={premiumConfig} delay={0} />
            <PricingCard premiumConfig={premiumConfig} delay={120} />
          </div>
          <p className="pricing-note" data-aos="fade-up"><i className="fa-solid fa-lock" /> Transition protégée : les artistes déjà inscrits conservent leurs fonctionnalités actuelles pendant 6 mois.</p>
        </section>

        <section className="final-cta" data-aos="fade-up">
          <div>
            <div className="eyebrow eyebrow-light">Le prochain chapitre vous appartient</div>
            <h2>Rejoignez la révolution<br /><em>musicale malgache.</em></h2>
            <p>Protégez votre art. Valorisez votre talent. Encaissez vos gains.</p>
          </div>
          {isLoggedIn ? (
            <Link to="/dashboard" className="button button-light"><i className="fa-solid fa-table-columns" /> Retourner à mon studio</Link>
          ) : (
            <Link to="/register" className="button button-light"><i className="fa-solid fa-bolt" /> Lancer mon projet maintenant</Link>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Home;
